import { useEffect, useState } from "react";
import { CheckCircle2, Circle, ClipboardList } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { EXPEDIENTE_CHECKLIST } from "@/constants/testIds";
import { toast } from "sonner";

// Leyenda al inicio del expediente: qué debe contener (documentos que CERH genera y ya
// firma/acusa por su cuenta, datos que ya se capturan en la ficha del empleado, y documentos
// externos que CERH no genera ni almacena) para que el admin sepa qué falta pedir en un alta
// y qué debe revisar que esté en cumplimiento.
export function ExpedienteChecklist({ employeeId, generatedDocs }) {
  const [checklist, setChecklist] = useState(null);
  const [comments, setComments] = useState({});
  // Estado local del checkbox por item, separado del que devuelve el servidor. Se actualiza
  // de inmediato al marcar/desmarcar (optimista) para que "Guardar nota" siempre lea el valor
  // vigente en pantalla y no un valor viejo de antes del toggle (causaba que guardar la nota
  // revirtiera la casilla al valor anterior).
  const [checkedState, setCheckedState] = useState({});

  const load = () => {
    api.get(`/employees/${employeeId}/expediente-checklist`).then(({ data }) => {
      setChecklist(data);
      setComments((prev) => {
        const next = { ...prev };
        data.externos.forEach((item) => {
          if (next[item.id] === undefined) next[item.id] = item.comment || "";
        });
        return next;
      });
      setCheckedState((prev) => {
        const next = { ...prev };
        data.externos.forEach((item) => {
          if (next[item.id] === undefined) next[item.id] = item.checked;
        });
        return next;
      });
    });
  };

  useEffect(load, [employeeId]);

  const saveItem = async (itemId, checked) => {
    try {
      await api.post(`/employees/${employeeId}/expediente-checklist/${itemId}`, {
        checked,
        comment: comments[itemId] || null,
      });
      toast.success("Checklist de expediente actualizado");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleChecked = (itemId, value) => {
    setCheckedState((prev) => ({ ...prev, [itemId]: value }));
    saveItem(itemId, value);
  };

  if (!checklist) return null;

  const totalGenerados = generatedDocs?.length || 0;
  const firmadosGenerados = generatedDocs?.filter((d) => d.status === "signed" || d.manual_confirmation).length || 0;

  return (
    <div data-testid={EXPEDIENTE_CHECKLIST.root} className="bg-card border border-petroleum/10 rounded-2xl p-5 mb-4">
      <div className="flex items-center gap-2 mb-1">
        <ClipboardList className="w-4 h-4 text-petroleum" strokeWidth={1.5} />
        <p className="font-heading font-semibold text-charcoal">Qué debe tener este expediente</p>
      </div>
      <p className="text-xs text-charcoal/50 mb-4">
        Referencia de lo que un expediente completo necesita. Lo que CERH genera ya lleva su propio
        control de firma; lo demás lo confirmas tú cuando lo tengas.
      </p>

      <div className="mb-4">
        <p className="text-xs font-semibold text-charcoal/60 uppercase tracking-wide mb-2">Generados en CERH</p>
        <p className="text-sm text-charcoal">
          {totalGenerados === 0
            ? "Aún no se ha generado ningún documento de firma para esta persona."
            : `${firmadosGenerados} de ${totalGenerados} firmados o confirmados. Detalle abajo, en Documentos.`}
        </p>
      </div>

      <div className="mb-4">
        <p className="text-xs font-semibold text-charcoal/60 uppercase tracking-wide mb-2">Datos capturados en el expediente</p>
        <div className="space-y-1.5">
          {checklist.auto.map((item) => (
            <div key={item.id} data-testid={EXPEDIENTE_CHECKLIST.autoItem} className="flex items-center gap-2 text-sm">
              {item.presente ? (
                <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0" strokeWidth={1.5} />
              ) : (
                <Circle className="w-4 h-4 text-amber-500 shrink-0" strokeWidth={1.5} />
              )}
              <span className={item.presente ? "text-charcoal" : "text-charcoal/60"}>
                {item.label}
                {!item.presente ? " — falta capturar en Editar expediente" : ""}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs font-semibold text-charcoal/60 uppercase tracking-wide mb-2">Documentos externos que debes tener</p>
        <div className="space-y-3">
          {checklist.externos.map((item) => (
            <div key={item.id} className="border border-petroleum/10 rounded-lg p-3">
              <label className="flex items-start gap-2 text-sm cursor-pointer">
                <Checkbox
                  data-testid={EXPEDIENTE_CHECKLIST.externalCheckbox}
                  checked={checkedState[item.id] ?? item.checked}
                  onCheckedChange={(v) => toggleChecked(item.id, !!v)}
                />
                <span>
                  {item.label}
                  {item.opcional ? <span className="text-charcoal/40"> (opcional)</span> : null}
                </span>
              </label>
              {item.updated_by && (
                <p className="text-xs text-charcoal/40 mt-1 ml-6">
                  {(checkedState[item.id] ?? item.checked) ? "Confirmado" : "Desmarcado"} por {item.updated_by}
                </p>
              )}
              <div className="flex items-center gap-2 mt-2 ml-6">
                <Input
                  data-testid={EXPEDIENTE_CHECKLIST.commentInput}
                  placeholder="Nota opcional, ej. guardado en expediente físico, firmado por ambas partes"
                  value={comments[item.id] ?? ""}
                  onChange={(e) => setComments((prev) => ({ ...prev, [item.id]: e.target.value }))}
                  className="text-xs h-8"
                />
                <Button
                  data-testid={EXPEDIENTE_CHECKLIST.saveCommentButton}
                  variant="outline"
                  size="sm"
                  className="h-8 shrink-0"
                  onClick={() => saveItem(item.id, checkedState[item.id] ?? item.checked)}
                >
                  Guardar nota
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
